'use client';

import { useController } from 'react-hook-form';
import type { DeepMap, FieldError, FieldValues, UseControllerProps } from 'react-hook-form';
import formStyle from '../../../styles/modules/form.module.scss';
import type { VariantType } from '../../../types';
import InputCheckbox from '../../input/InputCheckbox/InputCheckbox';
import FormLabel from '../FormLabel/FormLabel';
import styles from './FormCheckbox.module.scss';
import raffleStyles from './FormCheckbox.raffle.module.scss';

type Props<T extends FieldValues> = {
  label?: string;
  items: { value: string; child: React.ReactNode }[];
  required?: boolean;
  onChange?: () => void;
  hint?: string;
  variant?: VariantType;
  spacing?: 'narrow' | 'normal' | 'wide';
  isForceHideRequiredMark?: boolean;      // 必須マークを強制的に非表示にするかどうかのフラグ
} & UseControllerProps<T>;

function FormCheckbox<T extends FieldValues>({
  name,
  items,
  label,
  control,
  rules,
  onChange,
  hint,
  required,
  variant = 'default',
  spacing,
  isForceHideRequiredMark = false,
}: Props<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control, rules, defaultValue: [] as T[typeof name] });

  // --color-primaryの指定があるので分ける
  const currentStyles = variant === 'raffle' ? raffleStyles : styles;

  // 必須マークの表示判定
  const showRequiredMark = () => {
    // 強制的に必須マークを非表示にする場合はfalseを返す
    if (isForceHideRequiredMark) return false;
    // labelがなく必須の場合は必須マークを表示するためtrueを返す
    if (!label && required ) return true;
    // それ以外の場合はfalseを返す
    return false;
  }

  return (
    <div className={formStyle.formComponentStyle}>
      {/* ラベル */}
      {label && <FormLabel showRequiredMark={required}>{label}</FormLabel>}
      {/* フィールドブロック */}
      <div className={error ? formStyle.hasError : ''}>
        <div
          className={`${currentStyles.checkboxes} ${required && spacing === 'wide' ? currentStyles['required-wide'] : ''}`}
        >
          {items.map((item) => (
            <InputCheckbox
              key={item.value}
              name={field.name}
              inputRef={field.ref}
              checked={field.value.includes(item.value)}
              onChange={(e: React.ChangeEvent) => {
                if ((e.target as HTMLInputElement).checked) {
                  field.onChange(field.value.concat([item.value]));
                } else {
                  field.onChange(field.value.filter((fv: string) => fv !== item.value));
                }
                if (onChange) {
                  onChange();
                }
              }}
              inputValue={item.value}
              spacing={spacing}
            >
              {item.child}
            </InputCheckbox>
          ))}
          {/* 必須マークの表示判定がtrueの場合は必須マークを表示する */}
          {showRequiredMark() && <span className={currentStyles.requiredMarkOnly}>*</span>}
        </div>
        {hint && <p className={formStyle.hint}>{hint}</p>}
        {/* エラー */}
        {error && <p className={formStyle.validateErrors}>{(error as DeepMap<FieldValues, FieldError>).message}</p>}
      </div>
    </div>
  );
}

export default FormCheckbox;
